import { useEffect } from 'react';

const SITE_URL = 'https://nutrete.cl';
const SITE_NAME = 'Nútrete';
const DEFAULT_OG_IMAGE = `${SITE_URL}/NutreTe2.png`;

const SEO = ({
  title = 'Nútrete | Bienestar Corporativo con Nutrición y Fitoterapia en Chile',
  description = 'Programas de bienestar corporativo en Chile que combinan nutrición, fitoterapia y educación práctica: alimentación institucional, evaluaciones preventivas, talleres experienciales y aromaterapia aplicada.',
  ogImage = DEFAULT_OG_IMAGE,
  canonical,
}) => {
  useEffect(() => {
    document.title = title;

    const upsertMeta = (key, content, attribute = 'name') => {
      if (content === undefined || content === null) return;
      let element = document.querySelector(`meta[${attribute}="${key}"]`);
      if (!element) {
        element = document.createElement('meta');
        element.setAttribute(attribute, key);
        document.head.appendChild(element);
      }
      element.setAttribute('content', content);
    };

    const upsertLink = (rel, href) => {
      let element = document.querySelector(`link[rel="${rel}"]`);
      if (!element) {
        element = document.createElement('link');
        element.setAttribute('rel', rel);
        document.head.appendChild(element);
      }
      element.setAttribute('href', href);
    };

    const url = canonical || `${SITE_URL}${window.location.pathname}`;

    upsertMeta('description', description);
    upsertMeta('robots', 'index, follow, max-image-preview:large, max-snippet:-1');

    upsertLink('canonical', url);

    upsertMeta('og:type', 'website', 'property');
    upsertMeta('og:site_name', SITE_NAME, 'property');
    upsertMeta('og:locale', 'es_CL', 'property');
    upsertMeta('og:title', title, 'property');
    upsertMeta('og:description', description, 'property');
    upsertMeta('og:image', ogImage, 'property');
    upsertMeta('og:url', url, 'property');

    upsertMeta('twitter:card', 'summary_large_image');
    upsertMeta('twitter:title', title);
    upsertMeta('twitter:description', description);
    upsertMeta('twitter:image', ogImage);

    const structuredData = {
      '@context': 'https://schema.org',
      '@type': 'NutritionBusiness',
      name: SITE_NAME,
      description:
        'Programas de bienestar corporativo que combinan nutrición, fitoterapia y educación práctica para organizaciones en Chile.',
      url: SITE_URL,
      logo: `${SITE_URL}/NutreTe2.png`,
      image: `${SITE_URL}/NutreTe2.png`,
      founder: {
        '@type': 'Person',
        name: 'Natacha González',
        jobTitle: 'Nutricionista y fundadora',
      },
      priceRange: '$$',
      areaServed: { '@type': 'Country', name: 'Chile' },
      address: {
        '@type': 'PostalAddress',
        addressLocality: 'Santiago',
        addressCountry: 'CL',
      },
      contactPoint: {
        '@type': 'ContactPoint',
        telephone: '+56-9-6566-7828',
        contactType: 'customer service',
        email: 'contacto@nutrete.cl',
        availableLanguage: ['Spanish'],
      },
      hasOfferCatalog: {
        '@type': 'OfferCatalog',
        name: 'Líneas de negocio',
        itemListElement: [
          {
            '@type': 'Offer',
            itemOffered: {
              '@type': 'Service',
              name: 'Alimentación institucional',
              description:
                'Supervisión técnica de casinos de personal y acompañamiento en cumplimiento normativo.',
            },
          },
          {
            '@type': 'Offer',
            itemOffered: {
              '@type': 'Service',
              name: 'Bienestar corporativo',
              description:
                'Programas integrales: evaluaciones preventivas de salud, charlas y talleres experienciales, diagnóstico organizacional y medición.',
            },
          },
          {
            '@type': 'Offer',
            itemOffered: {
              '@type': 'Service',
              name: 'Nutrición, fitoterapia y aromaterapia',
              description:
                'Integración de plantas medicinales y aromaterapia en programas de bienestar, con actividades presenciales y online.',
            },
          },
        ],
      },
    };

    document
      .querySelectorAll('script[type="application/ld+json"][data-seo="nutrete-business"]')
      .forEach((node) => node.remove());

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.dataset.seo = 'nutrete-business';
    script.text = JSON.stringify(structuredData);
    document.head.appendChild(script);
  }, [title, description, ogImage, canonical]);

  return null;
};

export default SEO;
